// apps/client/src/components/AiFieldEnhancer.tsx

import { FieldEnhancementRequest } from '@avanzu/types';
import { AutoFixHigh, CheckCircle, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { apiClient } from '../../../services';
import { getComplianceColor } from '../../../utils/compliance';

interface AiFieldEnhancerProps {
  organisationId: string;
  projectId: string;
  /** FieldDef.key (e.g. "core.hypothesis") — freeform now that fields are schema-driven. */
  fieldName: string;
  currentValue: string;
  projectContext: {
    name?: string;
    description?: string;
    industrySector?: string;
    hypothesis?: string;
    technicalUncertainty?: string;
    objectives?: string;
    activityName?: string;
    activityType?: string;
  };
  onApply: (enhancedValue: string) => void;
  disabled?: boolean;
  quotaAiEnabled?: boolean;
}

export const AiFieldEnhancer: React.FC<AiFieldEnhancerProps> = ({
  organisationId,
  projectId,
  fieldName,
  currentValue,
  projectContext,
  onApply,
  disabled = false,
  quotaAiEnabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enhancedText, setEnhancedText] = useState('');
  const [complianceScore, setComplianceScore] = useState<number | null>(null);
  const [changes, setChanges] = useState<string[]>([]);
  const [issues, setIssues] = useState<string[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  const handleEnhance = async () => {
    if (!currentValue || currentValue.trim().length === 0) {
      enqueueSnackbar('Cannot enhance empty field', { variant: 'warning' });
      return;
    }

    setOpen(true);
    setLoading(true);

    try {
      const requestBody: FieldEnhancementRequest = {
        fieldName,
        currentValue,
        projectContext,
      };

      const response = await apiClient.post(
        `/organisations/${organisationId}/projects/${projectId}/copilot/enhance`,
        requestBody
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to enhance field');
      }

      const data = await response.json();

      setEnhancedText(data.enhancedText);
      setComplianceScore(data.complianceScore);
      setChanges(data.changes || []);
      setIssues(data.issues || []);
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to enhance field', {
        variant: 'error',
      });
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    onApply(enhancedText);
    setOpen(false);
    enqueueSnackbar('Field enhanced successfully', { variant: 'success' });
  };

  const handleClose = () => {
    setOpen(false);
  };

  const isDisabled =
    disabled || !currentValue || currentValue.trim().length === 0 || !quotaAiEnabled;
  const buttonText = !quotaAiEnabled ? 'Subscribe to enable AI' : 'Enhance';
  const tooltipText = !quotaAiEnabled
    ? 'AI features require a subscription. Please upgrade your plan to access AI-powered enhancements.'
    : 'Enhance with AI to improve AusIndustry compliance';

  return (
    <>
      <Tooltip title={tooltipText}>
        <span>
          <Button
            size="small"
            startIcon={<AutoFixHigh />}
            onClick={handleEnhance}
            disabled={isDisabled}
            variant="text"
            sx={{ textTransform: 'none' }}
          >
            {buttonText}
          </Button>
        </span>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <AutoFixHigh color="primary" />
              <Typography variant="h6">AI Enhancement</Typography>
            </Box>
            <IconButton size="small" onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {loading ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <CircularProgress size={48} />
              <Typography variant="body2" color="text.secondary" mt={2}>
                Enhancing your text for AusIndustry compliance...
              </Typography>
            </Box>
          ) : (
            <Stack spacing={3}>
              {/* Compliance Score */}
              {complianceScore !== null && (
                <Box
                  sx={{
                    bgcolor: `${getComplianceColor(complianceScore)}.light`,
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: `${getComplianceColor(complianceScore)}.main`,
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Compliance Score: {complianceScore}/100
                  </Typography>
                  {complianceScore >= 75 ? (
                    <Typography variant="caption" color="text.secondary">
                      ✓ This text meets AusIndustry compliance standards
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      This text could be further improved for compliance
                    </Typography>
                  )}
                </Box>
              )}

              {/* Changes Made */}
              {changes.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Improvements Made:
                  </Typography>
                  <List dense>
                    {changes.map((change, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={change}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Enhanced Text */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Enhanced Text:
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  value={enhancedText}
                  onChange={(e) => setEnhancedText(e.target.value)}
                  variant="outlined"
                  helperText="You can edit this text before applying it"
                />
              </Box>

              {/* Remaining Issues */}
              {issues.length > 0 && (
                <Box
                  sx={{
                    bgcolor: 'warning.light',
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'warning.main',
                  }}
                >
                  <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>
                    Suggestions for further improvement:
                  </Typography>
                  {issues.map((issue, idx) => (
                    <Typography key={idx} variant="caption" display="block">
                      • {issue}
                    </Typography>
                  ))}
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            disabled={loading}
            startIcon={<CheckCircle />}
          >
            Apply Enhancement
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AiFieldEnhancer;
